import { addDays, format, parseISO } from 'date-fns';
import { LotusBlossomPurchase, PurchasesByEmail } from 'api/lotusBlossom/lotusBlossomAPI';
import { ThemeSession } from './themeLists';

/**
 * Matched on themeStartDate where the checkout recorded one; older purchases
 * carry only a theme name, so that is the fallback — limited to purchases made
 * by the session's last day, or last year's booking of the same theme and tier
 * would label this year's. Ties go to the earliest purchase, which is the one
 * that bought the seat.
 */
export function findSessionPurchase(
	purchases: LotusBlossomPurchase[] | undefined,
	session: ThemeSession
): LotusBlossomPurchase | null {
	if (!purchases?.length) return null;

	const payments = purchases.filter((p) => p.kind === 'payment');
	const dated = payments.filter((p) => p.themeStartDate === session.dateISO);
	// Midnight after the last day; addDays keeps it local across a DST change.
	const sessionEnds = addDays(session.endDate, 1).getTime();
	const candidates = dated.length
		? dated
		: payments.filter(
				(p) =>
					!p.themeStartDate &&
					p.themeName === session.themeName &&
					new Date(p.date).getTime() < sessionEnds
		  );

	if (!candidates.length) return null;

	return candidates.reduce((earliest, p) => (p.date < earliest.date ? p : earliest));
}

/**
 * Checkouts that never became a membership: the first payment failed or was
 * abandoned. Stripe keeps the record, but it says nothing about when someone
 * became a member.
 */
const NEVER_STARTED = ['incomplete', 'incomplete_expired'];

/** Subscription records that were real memberships, whatever their state now. */
export function memberships(
	purchases: LotusBlossomPurchase[] | undefined
): LotusBlossomPurchase[] {
	return (purchases ?? []).filter(
		(p) => p.kind === 'subscription' && !NEVER_STARTED.includes(p.status)
	);
}

/**
 * Swaps in a membership as the server returned it after a cancel or resume, so
 * the page reflects the change without paging Stripe again.
 */
export function replaceMembership(
	byEmail: PurchasesByEmail,
	updated: LotusBlossomPurchase
): PurchasesByEmail {
	if (!updated.subscriptionId) return byEmail;

	return Object.fromEntries(
		Object.entries(byEmail).map(([email, purchases]) => [
			email,
			purchases.map((p) =>
				p.kind === 'subscription' && p.subscriptionId === updated.subscriptionId
					? updated
					: p
			),
		])
	);
}

/**
 * When a membership began. Someone who lapsed and came back has more than one
 * subscription record, so the earliest is taken — that is when they became a
 * member.
 */
export function findMembershipStart(
	purchases: LotusBlossomPurchase[] | undefined
): LotusBlossomPurchase | null {
	const subscriptions = memberships(purchases);
	if (!subscriptions.length) return null;

	return subscriptions.reduce((earliest, p) => (p.date < earliest.date ? p : earliest));
}

/** Prefixes a member's date, so the column reads as a membership, not a sale. */
export const MEMBER_SINCE_PREFIX = 'Member since';

export interface SessionPurchase {
	/** What the roster shows, e.g. "Sep 1, 2026" or "Member since Mar 4, 2025". */
	label: string;
	/** ISO timestamp behind the label, for sorting; '' when unknown. */
	date: string;
}

const NO_PURCHASE: SessionPurchase = { label: '', date: '' };

/**
 * Members get the date their membership started rather than a purchase date,
 * since the membership — not a booking — is what covers the session.
 *
 * Blank when nothing was bought and no start date is known: free enrolments,
 * anyone added by hand, and members whose subscription predates Stripe.
 */
export function sessionPurchase(
	byEmail: PurchasesByEmail,
	email: string,
	session: ThemeSession,
	isMember: boolean
): SessionPurchase {
	if (!email) return NO_PURCHASE;

	const purchases = byEmail[email.trim().toLowerCase()];

	if (isMember) {
		const membership = findMembershipStart(purchases);
		if (!membership) return NO_PURCHASE;

		return {
			label: `${MEMBER_SINCE_PREFIX} ${format(parseISO(membership.date), 'MMM d, yyyy')}`,
			date: membership.date,
		};
	}

	const purchase = findSessionPurchase(purchases, session);
	if (!purchase) return NO_PURCHASE;

	return { label: format(parseISO(purchase.date), 'MMM d, yyyy'), date: purchase.date };
}
